
import type React from "react"
import { useState, useRef, type ChangeEvent, type DragEvent, useEffect } from "react"
import { uploadMedia } from "../../operationalRequest"
import { Trash2, Upload, X, Maximize2, Minimize2, Crop } from "lucide-react"
import { Progress, Slider, Switch } from "antd"
import CropOverlay from "./CropOverlay"
import { cropImageFile, resizeImageFile } from "./crop-utils"

interface ImageUploadProps {
    allowMultiple?: boolean
    maxFileSize?: number // in MB
    initialFiles?: File[]
    onUploadComplete?: (result: any) => void
    enableResize?: boolean
    enableCropping?: boolean
    apiEndpoint: string
    title?: string
}

const ImageUpload: React.FC<ImageUploadProps> = ({
    allowMultiple = false,
    maxFileSize = 5,
    initialFiles = [],
    onUploadComplete,
    enableResize = false,
    enableCropping = false,
    apiEndpoint,
    title = "Image Upload",
}) => {
    const [files, setFiles] = useState<File[]>(initialFiles)
    const [previews, setPreviews] = useState<string[]>([])
    const [uploading, setUploading] = useState(false)
    const [message, setMessage] = useState("")
    const [messageType, setMessageType] = useState<"success" | "error" | "info">("info")
    const [uploadProgress, setUploadProgress] = useState(0)
    const [isDragging, setIsDragging] = useState(false)
    const [resizePercentage, setResizePercentage] = useState(100)
    const [multipleMode, setMultipleMode] = useState(allowMultiple)

    // Cropping state
    const [cropMode, setCropMode] = useState(false)
    const [cropImageIndex, setCropImageIndex] = useState<number | null>(null)
    const [cropAspectRatio, setCropAspectRatio] = useState<number | undefined>(undefined)
    const [cropData, setCropData] = useState<{ x: number; y: number; width: number; height: number } | null>(null)

    const fileInputRef = useRef<HTMLInputElement>(null)

    // Initialize multipleMode from props
    useEffect(() => {
        setMultipleMode(allowMultiple)
    }, [allowMultiple])

    // Generate previews when files change
    useEffect(() => {
        const newPreviews = files.map((file) => URL.createObjectURL(file))
        setPreviews(newPreviews)

        // Cleanup function to revoke object URLs
        return () => {
            newPreviews.forEach((preview) => URL.revokeObjectURL(preview))
        }
    }, [files])

    const showMessage = (text: string, type: "success" | "error" | "info" = "info") => {
        setMessage(text)
        setMessageType(type)

        // Auto-clear success and info messages after 5 seconds
        if (type !== "error") {
            setTimeout(() => {
                setMessage("")
            }, 5000)
        }
    }

    const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
        e.stopPropagation(); // prevent any bubbling
        e.preventDefault();
        if (e.target.files) {
            const selectedFiles = Array.from(e.target.files).filter((f) => f instanceof File)

            // Validate file size
            const oversizedFiles = selectedFiles.filter((file) => file.size > maxFileSize * 1024 * 1024)
            if (oversizedFiles.length > 0) {
                showMessage(`Some files exceed the ${maxFileSize}MB limit and were not added.`, "error")
                const validFiles = selectedFiles.filter((file) => file.size <= maxFileSize * 1024 * 1024)
                processFiles(validFiles)
                return
            }

            processFiles(selectedFiles)
        }
    }

    const processFiles = (newFiles: File[]) => {
        if (!multipleMode) {
            // Replace existing files in single mode
            setFiles(newFiles.slice(0, 1))
        } else {
            // Add to existing files in multiple mode
            setFiles((prev) => [...prev, ...newFiles])
        }
        showMessage(`${newFiles.length} file(s) added.`, "success")
    }

    const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault()
        setIsDragging(true)
    }

    const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault()
        setIsDragging(false)
    }

    const handleDrop = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault()
        setIsDragging(false)

        if (e.dataTransfer.files) {
            const droppedFiles = Array.from(e.dataTransfer.files).filter((f) => f.type.startsWith("image/"))

            if (droppedFiles.length === 0) {
                showMessage("Please drop image files only.", "error")
                return
            }

            // Validate file size
            const oversizedFiles = droppedFiles.filter((file) => file.size > maxFileSize * 1024 * 1024)
            if (oversizedFiles.length > 0) {
                showMessage(`Some files exceed the ${maxFileSize}MB limit and were not added.`, "error")
                const validFiles = droppedFiles.filter((file) => file.size <= maxFileSize * 1024 * 1024)
                processFiles(validFiles)
                return
            }

            processFiles(droppedFiles)
        }
    }

    const removeFile = (index: number) => {
        setFiles((prev) => prev.filter((_, i) => i !== index))
        setPreviews((prev) => prev.filter((_, i) => i !== index))
    }

    const triggerFileInput = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click()
        }
    }

    const startCropping = (index: number) => {
        setCropImageIndex(index)
        setCropMode(true)
        setCropData(null)
    }

    const handleCropChange = (data: { x: number; y: number; width: number; height: number }) => {
        setCropData(data)
    }

    const applyCrop = async () => {
        if (cropImageIndex === null || !cropData) {
            setCropMode(false)
            return
        }

        try {
            const file = files[cropImageIndex]
            const croppedFile = await cropImageFile(file, cropData)

            // Update the file in the files array
            const newFiles = [...files]
            newFiles[cropImageIndex] = croppedFile
            setFiles(newFiles)

            // Update the preview
            URL.revokeObjectURL(previews[cropImageIndex])
            const newPreviews = [...previews]
            newPreviews[cropImageIndex] = URL.createObjectURL(croppedFile)
            setPreviews(newPreviews)

            showMessage("Image cropped successfully", "success")
        } catch (error) {
            console.error("Cropping error:", error)
            showMessage("Failed to crop image", "error")
        } finally {
            setCropMode(false)
            setCropImageIndex(null)
            setCropData(null)
        }
    }

    const cancelCrop = () => {
        setCropMode(false)
        setCropImageIndex(null)
        setCropData(null)
    }

    const toggleMultipleMode = () => {
        if (!allowMultiple) return

        if (multipleMode && files.length > 1) {
            // If switching from multiple to single and we have multiple files,
            // keep only the first file
            setFiles([files[0]])
            setPreviews([previews[0]])
        }
        setMultipleMode(!multipleMode)
    }

    const handleUpload = async () => {
        if (files.length === 0) {
            showMessage("Please select files first.", "error")
            return
        }

        setUploading(true)
        setUploadProgress(0)
        showMessage("Starting upload...", "info")

        try {
            let filesToUpload = [...files]

            // Apply resizing if enabled and not at 100%
            if (enableResize && resizePercentage !== 100) {
                const resizePromises = filesToUpload.map((file) => resizeImageFile(file, resizePercentage))
                filesToUpload = await Promise.all(resizePromises)
            }

            // Mock progress updates
            const progressInterval = setInterval(() => {
                setUploadProgress((prev) => {
                    const newProgress = prev + Math.random() * 10
                    return newProgress > 90 ? 90 : newProgress
                })
            }, 300)

            const result = await uploadMedia(filesToUpload, apiEndpoint)

            clearInterval(progressInterval)
            setUploadProgress(100)

            showMessage("Upload successful!", "success")
            console.log("Upload result:", result)

            if (onUploadComplete) {
                onUploadComplete(result)
            }

            // Clear files after successful upload if desired
            // setFiles([]);
            // setPreviews([]);
        } catch (error) {
            console.error("Upload error:", error)
            showMessage("Upload failed. Please try again.", "error")
        } finally {
            setUploading(false)
            // Reset progress after a delay
            setTimeout(() => setUploadProgress(0), 2000)
        }
    }

    return (
        <div className="p-6 border rounded-lg shadow-sm w-full max-w-2xl mx-auto bg-surface">
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold">{title}</h2>

                {/* Settings Area */}
                {/* <div className="flex items-center space-x-4">
          {allowMultiple && (
            <div className="flex items-center space-x-2">
              <span className="text-sm font-medium">Multiple:</span>
              <Switch checked={multipleMode} onChange={toggleMultipleMode} />
            </div>
          )}
        </div> */}
            </div>

            {/* Drag & Drop Area */}
            <div
                className={`border-2 border-dashed rounded-lg p-8 mb-6 text-center transition-colors
          ${isDragging ? "border-blue-500 bg-blue-50" : "border-gray-300 hover:border-gray-400"}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={triggerFileInput}
            >
                <input
                    type="file"
                    ref={fileInputRef}
                    accept="image/*"
                    multiple={multipleMode}
                    onChange={handleFileChange}
                    style={{ pointerEvents: "none" }}
                    className="hidden"
                />

                <div className="flex flex-col items-center">
                    <Upload className="h-12 w-12 text-gray-400 mb-3" />
                    <p className="text-lg font-medium mb-1">Drag and drop your images here</p>
                    <p className="text-sm text-gray-500 mb-3">or click to browse</p>
                    <p className="text-xs text-gray-400">
                        {multipleMode ? "Upload multiple images" : "Upload a single image"} (Max: {maxFileSize}MB per file)
                    </p>
                </div>
            </div>

            {/* Resize Controls (if enabled) */}
            {enableResize && (
                <div className="mb-6">
                    <div className="flex justify-between items-center mb-2">
                        <span className="text-sm font-medium">Resize Images</span>
                        <span className="text-sm text-gray-500">{resizePercentage}%</span>
                    </div>
                    <div className="flex items-center space-x-4">
                        <Minimize2 className="h-4 w-4 text-gray-500" />
                        <Slider
                            value={resizePercentage}
                            min={10}
                            max={100}
                            onChange={(value: any) => setResizePercentage(value)}
                            className="flex-1"
                        />
                        <Maximize2 className="h-4 w-4 text-gray-500" />
                    </div>
                </div>
            )}

            {/* Preview Area */}
            {previews.length > 0 && (
                <div className="mb-6">
                    <h3 className="text-sm font-medium mb-3">Selected Images ({files.length})</h3>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                        {previews.map((preview, index) => (
                            <div key={index} className="relative group">
                                <div className="aspect-square rounded-md overflow-hidden border bg-gray-50">
                                    <img
                                        src={preview || "/placeholder.svg"}
                                        alt={`Preview ${index + 1}`}
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                                <div className="absolute top-1 right-1 flex space-x-1">
                                    {enableCropping && (
                                        <button
                                            type="button"
                                            onClick={(e) => {
                                                e.stopPropagation()
                                                startCropping(index)
                                            }}
                                            className="bg-blue-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                                            aria-label="Crop image"
                                        >
                                            <Crop className="h-3 w-3" />
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            removeFile(index)
                                        }}
                                        className="bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                                        aria-label="Remove image"
                                    >
                                        <X className="h-3 w-3" />
                                    </button>
                                </div>
                                <p className="text-xs text-gray-500 truncate mt-1">{files[index].name}</p>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Cropping Interface */}
            {enableCropping && cropMode && cropImageIndex !== null && previews[cropImageIndex] && (
                <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg p-4 max-w-4xl w-full">
                        <h3 className="text-lg font-medium mb-4">Crop Image</h3>

                        <CropOverlay
                            imageUrl={previews[cropImageIndex]}
                            onCropChange={handleCropChange}
                            aspectRatio={cropAspectRatio}
                        />

                        <div className="mt-4 flex justify-between">
                            <div className="flex items-center space-x-4">
                                <button
                                    onClick={() => setCropAspectRatio(undefined)}
                                    className={`px-3 py-1 text-sm rounded ${!cropAspectRatio ? "bg-blue-100 text-blue-700" : "bg-gray-100"}`}
                                >
                                    Free
                                </button>
                                <button
                                    onClick={() => setCropAspectRatio(1)}
                                    className={`px-3 py-1 text-sm rounded ${cropAspectRatio === 1 ? "bg-blue-100 text-blue-700" : "bg-gray-100"}`}
                                >
                                    1:1
                                </button>
                                <button
                                    onClick={() => setCropAspectRatio(4 / 3)}
                                    className={`px-3 py-1 text-sm rounded ${cropAspectRatio === 4 / 3 ? "bg-blue-100 text-blue-700" : "bg-gray-100"}`}
                                >
                                    4:3
                                </button>
                                <button
                                    onClick={() => setCropAspectRatio(16 / 9)}
                                    className={`px-3 py-1 text-sm rounded ${cropAspectRatio === 16 / 9 ? "bg-blue-100 text-blue-700" : "bg-gray-100"}`}
                                >
                                    16:9
                                </button>
                            </div>

                            <div className="flex space-x-2">
                                <button
                                    onClick={cancelCrop}
                                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                                <button onClick={applyCrop} className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700">
                                    Apply Crop
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Upload Progress */}
            {uploadProgress > 0 && (
                <div className="mb-4">
                    <div className="flex justify-between text-xs mb-1">
                        <span>Uploading...</span>
                        <span>{Math.round(uploadProgress)}%</span>
                    </div>
                    <Progress percent={uploadProgress} className="h-2" />
                </div>
            )}

            {/* Message Area */}
            {message && (
                <div
                    className={`mb-4 p-3 rounded text-sm ${messageType === "error"
                            ? "bg-red-50 text-red-700"
                            : messageType === "success"
                                ? "bg-green-50 text-green-700"
                                : "bg-blue-50 text-blue-700"
                        }`}
                >
                    {message}
                </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-between">
                <button
                    type="button"
                    onClick={() => {
                        setFiles([])
                        setPreviews([])
                        setMessage("")
                    }}
                    disabled={uploading || files.length === 0}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Clear All
                </button>

                <button
                    onClick={handleUpload}
                    disabled={uploading || files.length === 0}
                    className="bg-primary text-primaryText px-6 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                    {uploading ? (
                        <>
                            <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                            Uploading...
                        </>
                    ) : (
                        <>
                            <Upload className="h-4 w-4 mr-2" />
                            Upload
                        </>
                    )}
                </button>
            </div>
        </div>
    )
}

export default ImageUpload
